import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'

import type { CollectionProducts } from '@lib/types'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'
import { trackGetStartedEvent } from '@lib/analytics/get-started-tracking'

import Logo from '@components/global/Logo'

import LandingProductCarousel from './LandingProductCarousel'

type Props = {
	onComplete: () => void
	collectionProducts?: CollectionProducts
	pageContent?: GetStartedPageContent
}

const HOW_IT_WORKS_STEPS = [
	{
		number: '1',
		title: 'Take a quick quiz',
		body: 'Tell us about your skin, hair, and what you\'re currently loving.',
	},
	{
		number: '2',
		title: 'Choose your products',
		body: 'Each month, browse that month\'s full lineup and hand-pick what fills every spot in your box.',
	},
	{
		number: '3',
		title: 'We pack and ship it',
		body: 'Your box arrives every month. Pause or cancel anytime — no fees.',
	},
]

export default function GetStartedLandingVariant({ onComplete, collectionProducts, pageContent: pageContentProp }: Props) {
	const router = useRouter()
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT

	const [slide, setSlide] = useState(0) // 0 = How It Works, 1 = This Month's Box
	const [cardsVisible, setCardsVisible] = useState(false)

	const prefersReducedMotion =
		typeof window !== 'undefined'
			? window.matchMedia('(prefers-reduced-motion: reduce)').matches
			: false

	useEffect(() => {
		if (slide === 0) {
			trackGetStartedEvent('survey_landing_clarity_slide1_viewed')
			const timer = setTimeout(() => setCardsVisible(true), 50)
			return () => clearTimeout(timer)
		}
		trackGetStartedEvent('survey_landing_clarity_slide2_viewed')
		return undefined
	}, [slide])

	const handleSlide1CTA = useCallback(() => {
		trackGetStartedEvent('survey_landing_clarity_slide1_advanced')
		setCardsVisible(false)
		setSlide(1)
	}, [])

	const handleSlide2CTA = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.currentTarget.style.pointerEvents = 'none'
		trackGetStartedEvent('survey_landing_clarity_slide2_advanced')
		onComplete()
	}, [onComplete])

	return (
		<div className="flex flex-col h-[100dvh] bg-white">
			{/* Fixed top: Logo */}
			<div className="flex-shrink-0 bg-white">
				<div className="container">
					<div className="flex justify-center py-3 md:py-5">
						<button
							type="button"
							aria-label="Birchbox Home"
							className="cursor-pointer"
							onClick={() => {
								if (typeof window !== 'undefined' && window.history.length > 1) {
									router.back()
								} else {
									router.push('/')
								}
							}}
						>
							<Logo className="w-28 md:w-32 fill-bbx-style" />
						</button>
					</div>
				</div>
			</div>

			{/* Scrollable content */}
			<div className="flex-1 overflow-y-auto bg-white">
				<div className="min-h-full flex flex-col justify-center">

					{slide === 0 && (
						<div className="px-4 pt-6 pb-10 text-center container max-w-2xl mx-auto">
							<p className="text-xs font-bold text-bbx-spark uppercase tracking-widest mb-2">
								How it works
							</p>
							<h1 className="text-2xl md:text-4xl font-serif font-medium leading-tight mb-2">
								Your box, every month.
							</h1>
							<p className="text-sm text-gray-500 mb-5">
								You choose what goes in — we handle the rest.
							</p>

							<div className="relative w-full h-36 rounded-2xl overflow-hidden mb-7">
								<Image
									src="/images/get-started-box-preview.avif"
									alt="Birchbox — a curated selection of beauty products"
									fill
									className="object-cover object-center"
									priority
								/>
							</div>

							<div className="flex flex-col text-left">
								{HOW_IT_WORKS_STEPS.map(({ number, title, body }, index) => {
									const isLast = index === HOW_IT_WORKS_STEPS.length - 1
									const baseDelay = prefersReducedMotion ? 0 : index * 160

									return (
										<div key={number} className="flex gap-4">
											{/* Left track: circle + connector line */}
											<div className="flex flex-col items-center flex-shrink-0 w-7">
												<div
													className={`w-7 h-7 rounded-full bg-bbx-glimmer flex items-center justify-center transition-all duration-500 ${
														cardsVisible || prefersReducedMotion
															? 'opacity-100 scale-100'
															: 'opacity-0 scale-50'
													}`}
													style={{ transitionDelay: `${baseDelay}ms` }}
												>
													<span className="text-xs font-bold text-white">{number}</span>
												</div>
												{!isLast && (
													<div
														className={`w-px bg-bbx-glimmer flex-1 my-1.5 origin-top transition-transform duration-500 opacity-20 ${
															cardsVisible || prefersReducedMotion ? 'scale-y-100' : 'scale-y-0'
														}`}
														style={{ transitionDelay: `${baseDelay + 120}ms` }}
													/>
												)}
											</div>

											{/* Step text */}
											<div
												className={`${isLast ? 'pb-1' : 'pb-5'} transition-all duration-500 ${
													cardsVisible || prefersReducedMotion
														? 'opacity-100 translate-y-0'
														: 'opacity-0 translate-y-2'
												}`}
												style={{ transitionDelay: `${baseDelay + 60}ms` }}
											>
												<p className="text-sm font-bold text-bbx-style pt-0.5 leading-tight">{title}</p>
												<p className="text-xs text-gray-500 mt-1 leading-relaxed">{body}</p>
											</div>
										</div>
									)
								})}
							</div>
						</div>
					)}

					{slide === 1 && (
						<div>
							<div className="px-4 pt-6 pb-4 text-center container max-w-2xl mx-auto">
								<p className="text-xs font-bold text-bbx-spark uppercase tracking-widest mb-2">
									This month&apos;s lineup
								</p>
								<h1 className="text-2xl md:text-4xl font-serif font-medium leading-tight mb-1">
									{new Date().toLocaleString('en-US', { month: 'long' })}&apos;s box
								</h1>
								<p className="text-sm text-gray-500">
									Up to $110 in full-size products — yours to choose from.
								</p>
							</div>

							{collectionProducts && collectionProducts.length > 0 && (
								<div className="pb-4">
									<p className="text-[11px] text-gray-500 text-center mb-3 px-4">
										Showing a few highlights — there&apos;s plenty more to explore.
									</p>
									<LandingProductCarousel
										products={collectionProducts}
										moreTitle={pageContent.carouselMoreTitle}
										moreSubtext={pageContent.carouselMoreSubtext}
									/>
								</div>
							)}

							<div className="flex justify-center pb-4">
								<div className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-1.5">
									<span className="text-xs text-gray-600">
										❤️ Trusted by 20,000+ beauty fans
									</span>
								</div>
							</div>
						</div>
					)}

				</div>
			</div>

			{/* Progress dots */}
			<div className="flex-shrink-0 flex justify-center gap-2 py-3 bg-white">
				<span
					className={`w-2 h-2 rounded-full transition-colors duration-300 ${
						slide === 0 ? 'bg-bbx-glimmer' : 'bg-gray-200'
					}`}
					aria-hidden="true"
				/>
				<span
					className={`w-2 h-2 rounded-full transition-colors duration-300 ${
						slide === 1 ? 'bg-bbx-glimmer' : 'bg-gray-200'
					}`}
					aria-hidden="true"
				/>
			</div>

			{/* Fixed bottom: CTA */}
			<div className="flex-shrink-0 bg-white border-t border-gray-100 pb-[env(safe-area-inset-bottom)]">
				<div className="px-4 py-4 md:px-12 max-w-4xl mx-auto w-full md:flex md:justify-center container">
					{slide === 0 ? (
						<button
							type="button"
							className="button button-action btn-lg w-full md:w-auto md:min-w-[200px]"
							onClick={handleSlide1CTA}
						>
							See this month&apos;s box →
						</button>
					) : (
						<button
							type="button"
							className="button button-action btn-lg animate-cta-attention w-full md:w-auto md:min-w-[200px]"
							onClick={handleSlide2CTA}
						>
							Start my quiz →
						</button>
					)}
				</div>
			</div>
		</div>
	)
}
